// src/context/SubscriberContext.js
// Mirrors PatientContext's pattern exactly, for Guardian Dubai subscriber
// sessions — a third principal type, distinct from staff (AuthContext) and
// patients (PatientContext).
//
// Shares the 'previu_token' localStorage key with staff/patient sessions
// (same existing convention — only one session is ever active per browser,
// api.js's request interceptor attaches whichever token is present). Uses
// its own 'previu_subscriber' key for profile data, same as 'previu_patient'.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dropPushSubscription } from '../utils/pushSubscription';

const SubscriberContext = createContext(null);

export function SubscriberProvider({ children }) {
  const navigate = useNavigate();
  const [subscriber, setSubscriber] = useState(() => {
    try {
      const raw = localStorage.getItem('previu_subscriber');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // Keep in sync if another tab logs in/out — same pattern as PatientContext.
  useEffect(() => {
    function onStorage(e) {
      if (e.key === 'previu_subscriber') {
        try {
          setSubscriber(e.newValue ? JSON.parse(e.newValue) : null);
        } catch { setSubscriber(null); }
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Called by SubscriberLoginPage/SubscriberRegisterPage after a successful
  // /api/subscriber-auth/login or /register response.
  function storeSession(authResponse) {
    const { token, ...profile } = authResponse;
    localStorage.setItem('previu_token', token);
    localStorage.setItem('previu_subscriber', JSON.stringify(profile));
    setSubscriber(profile);
  }

  function logout() {
    dropPushSubscription({ notifyServer: true }); // see PatientContext.logout
    localStorage.removeItem('previu_token');
    localStorage.removeItem('previu_subscriber');
    setSubscriber(null);
    navigate('/subscriber-login');
  }

  // Same-tab localStorage writes don't fire the 'storage' event (see
  // PatientContext's identical note) — call after storeSession if a
  // component needs subscriber.subscriberId synchronously in the same tick.
  function refreshSubscriber() {
    try {
      const raw = localStorage.getItem('previu_subscriber');
      setSubscriber(raw ? JSON.parse(raw) : null);
    } catch { setSubscriber(null); }
  }

  return (
    <SubscriberContext.Provider value={{ subscriber, storeSession, logout, refreshSubscriber }}>
      {children}
    </SubscriberContext.Provider>
  );
}

export function useSubscriber() {
  return useContext(SubscriberContext);
}
